import React, { useRef, useEffect, useState } from "react";
import { CTBRetestLog } from "../../../utils/api/endpoints/retests/parseRetests";
import { LogItem } from "./LogItems";

interface LogListProps {
  logs: CTBRetestLog[];
  className?: string;
  researcherUserId?: number;
  researcherUsername?: string;
  footer?: React.ReactNode;
}

export const LogList: React.FC<LogListProps> = ({
  logs,
  className = "w-full",
  researcherUserId,
  researcherUsername,
  footer
}) => {
  const logContainerRef = useRef<HTMLDivElement | null>(null);
  const [lineHeight, setLineHeight] = useState<number>(0);
  const [maxScrollHeight, setMaxScrollHeight] = useState<number>(450);

  useEffect(() => {
    if (logContainerRef.current) {
      // Calculate the distance between the first and last pfp
      const logItems = logContainerRef.current.querySelectorAll(".log-item");
      if (logItems.length > 1) {
        const firstPfp = logItems[0].querySelector(".pfp");
        const lastPfp = logItems[logItems.length - 1].querySelector(".pfp");
        if (firstPfp && lastPfp) {
          const firstPfpRect = firstPfp.getBoundingClientRect();
          const lastPfpRect = lastPfp.getBoundingClientRect();
          const height = lastPfpRect.top - firstPfpRect.top;
          setLineHeight(height);
        }
      }

      // Dynamically size scroll area to approximately 3 recent comments
      if (logItems.length > 0) {
        let heightSum = 0;
        const itemsToMeasure = Math.min(3, logItems.length);
        for (let i = 0; i < itemsToMeasure; i++) {
          const rect = (logItems[i] as HTMLElement).getBoundingClientRect();
          heightSum += rect.height;
        }
        // Add spacing/gaps padding
        const gapAllowance = 24 * (itemsToMeasure - 1); // tailwind space-y-6 ~ 24px
        const topPadding = 8 * 4; // rough padding around content
        const computed = Math.max(200, Math.round(heightSum + gapAllowance + topPadding));
        setMaxScrollHeight(computed);
      }
    }
  }, [logs]);

  return (
    <div
      className={`relative rounded-lg bg-white py-4 flex flex-col ${className}`}
      ref={logContainerRef}
    >
      <h3 className="mb-2 mt-2 pl-6 text-xl font-semibold text-black">
        Comments
      </h3>
      <div className="w-full border-t mb-4" style={{ borderColor: '#E5E7EB', borderWidth: 1 }} />
      
      {/* Scrollable comments container - shows ~3 comments by default with scrollbar */}
      <div className="space-y-6 overflow-y-auto pr-2" style={{ maxHeight: `${maxScrollHeight}px` }}>
        {logs
          .slice()
          .reverse()
          .map(log => (
            <LogItem
              key={log.id}
              log={log}
              researcherUserId={researcherUserId}
              researcherUsername={researcherUsername}
            />
          ))}
      </div>

      {footer && <div className="mt-6 px-6">{footer}</div>}
    </div>
  );
};
